import React, { FC, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
// components
import { PreStateLayout } from "./Components/PreState";
import { PostStateLayout } from "./Components/PostState";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
// types
import { M2CSwiggyOneHalfCardProps, States } from "./types";
// utils
import { isEmpty } from "@swiggy-private/js-utils";
import {
    M2C_HALFCARD_TEST_IDS,
    CLOSED_CTA_TEXT,
    DEFAULT_PROGRESS_DURATION,
    POST_STATE_CLOSE_CTA_TEXT,
    THOUSAND,
} from "./constants";
// styles
import { PostStateHalfcard, PreStateHalfcard } from "./styles";
// context
import { M2CHalfcardContext } from "@super-ui/contexts";

const M2CSwiggyOneHalfCard: FC<M2CSwiggyOneHalfCardProps> = ({
    preStateData,
    postStateData,
    state = States.PRE_STATE,
    isOneLite = false,
    onClose,
}) => {
    const [stateToRender, setStateToRender] = useState(state);
    const [addingSuperToCart, setAddingSuperToCart] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const { duration = DEFAULT_PROGRESS_DURATION, setIsM2CGratificationShown } = postStateData || {};
    const {
        analyticsEvents: { onClickM2CHalfcardSkipCta },
    } = useContext(M2CHalfcardContext);

    // Used ref here to get the height of the preState to set the height of the postState to make the experience consistent
    const preStateHeightRef = useRef<HTMLDivElement>(null);
    const postStateHeightRef = useRef<number>(0);

    const isPreState = useMemo(
        () => stateToRender === States.PRE_STATE && !isEmpty(preStateData),
        [stateToRender, preStateData],
    );

    const isPostState = useMemo(
        () => stateToRender === States.POST_STATE || isEmpty(preStateData),
        [preStateData, stateToRender],
    );

    const onCloseAction = useCallback(
        (ctaText: string) => {
            setIsOpen(false);
            onClickM2CHalfcardSkipCta(ctaText);
            if (isPreState) {
                preStateData?.closeBottomSheet?.(ctaText);
            } else if (isPostState) {
                setIsM2CGratificationShown?.(true);
                postStateData?.closeBottomSheet?.(ctaText);
            }
            typeof onClose === "function" && onClose();
        },
        [
            isPostState,
            isPreState,
            onClickM2CHalfcardSkipCta,
            postStateData,
            preStateData,
            onClose,
            setIsM2CGratificationShown,
        ],
    );

    useEffect(() => {
        if (isPreState && addingSuperToCart && !isEmpty(postStateData)) {
            setStateToRender(States.POST_STATE);
            setAddingSuperToCart(false);
        }
    }, [duration, postStateData, stateToRender, addingSuperToCart, isPreState, isPostState, onCloseAction]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (isPostState) {
            timeout = setTimeout(() => {
                onCloseAction(POST_STATE_CLOSE_CTA_TEXT);
            }, duration * THOUSAND);
        }

        return () => clearTimeout(timeout);
    }, [duration, postStateData, stateToRender, addingSuperToCart, isPreState, isPostState, onCloseAction]);

    useEffect(() => {
        postStateHeightRef.current = Number(preStateHeightRef?.current?.offsetHeight);
    }, []);

    const preStateProps = useMemo(() => {
        if (!isEmpty(preStateData)) {
            return {
                ...preStateData,
                addingSuperToCart,
                setAddingSuperToCart,
                closeBottomSheet: onCloseAction,
                setStateToRender,
                preStateHeightRef,
                isOneLite,
            };
        }
    }, [addingSuperToCart, isOneLite, onCloseAction, preStateData]);

    const postStateProps = useMemo(() => {
        if (!isEmpty(postStateData)) {
            return {
                ...postStateData,
                isOneLite,
            };
        }
    }, [isOneLite, postStateData]);

    return (
        <HalfCard
            isOpen={isOpen}
            closeOnOverlayClick
            hasCustomPadding
            onClose={() => onCloseAction(CLOSED_CTA_TEXT)}
            overflowY="visible"
        >
            {!isEmpty(preStateProps) && isPreState && (
                <PreStateHalfcard data-testid={M2C_HALFCARD_TEST_IDS.HALFCARD} addingSuperToCart={isPostState}>
                    <PreStateLayout {...preStateProps} />
                </PreStateHalfcard>
            )}

            {!isEmpty(postStateProps) && isPostState && (
                <PostStateHalfcard
                    data-testid={M2C_HALFCARD_TEST_IDS.HALFCARD}
                    height={Number(postStateHeightRef?.current)}
                >
                    <PostStateLayout {...postStateProps} />
                </PostStateHalfcard>
            )}
        </HalfCard>
    );
};

export default memo(M2CSwiggyOneHalfCard);
